import React, { useEffect, useState } from "react";
import DataTableElement from "../../../components/elements/DataTableElement";
import CustomButtonOval from "../../../components/form/CustomButtonOval";
import { useNavigate, useParams } from "react-router-dom";
import { invoicesResource } from "../../../helpers/configRoutes/backend_helper";
import { currencyUSD } from "../../../helpers/forms/formater";
import PreviewPDF from "../../../components/modal/PreviewPdf";

const PaymentDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [tableLoad, setTableLoad] = useState({
        show: false,
        textLoad: "Loading invoice...",
    });
    const [invoice, setInvoice] = useState({});
    const [modalPdf, setModalPdf] = useState({ show: false, url: "" });

    const loadInvoice = async () => {
        setTableLoad((prev) => ({ ...prev, show: true }));
        const response = await invoicesResource("show", {}, id);
        if (response?.invoice) {
            setInvoice(response.invoice);
        }
        setTableLoad((prev) => ({ ...prev, show: false }));
    };

    useEffect(() => {
        loadInvoice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const updateInvoiceStatus = async (status) => {
        const response = await invoicesResource("put", { status }, id);
        if (response?.status && response.invoice) {
            setInvoice(response.invoice);
        }
    };

    const getPdfDetailFile = async () => {
        const response = await invoicesResource("show", { params: { pdf: true } }, id);
        if (response?.pdf_base64) {
            setModalPdf({ show: true, url: response.pdf_base64 });
        }
    };

    const summaryColumns = [
        {
            id: "invoice_number",
            name: "INVOICE",
            cell: (row) => <span className="font-mono font-semibold text-cs-dark">{row.invoice_number}</span>,
        },
        {
            id: "client",
            name: "CLIENT",
            selector: (row) => row.client_name || "-",
        },
        {
            id: "invoice_date",
            name: "INVOICE DATE",
            selector: (row) => row.invoice_date || "-",
        },
        {
            id: "due_date",
            name: "DUE DATE",
            selector: (row) => row.due_date || "-",
        },
        {
            id: "total",
            name: "TOTAL",
            cell: (row) => <span className="font-semibold text-cs-primary-dark">{currencyUSD(row.total || 0)}</span>,
        },
        {
            id: "status",
            name: "STATUS",
            cell: (row) => (
                <select
                    value={row.status || "Unpaid"}
                    onChange={(event) => updateInvoiceStatus(event.target.value)}
                    className="px-2 py-1 rounded-lg border border-cs-border bg-cs-bg text-xs"
                >
                    <option value="Unpaid">Unpaid</option>
                    <option value="Paid">Paid</option>
                </select>
            ),
        },
    ];

    const lineColumns = [
        {
            id: "client_work_order",
            name: "CLIENT WORK ORDER",
            cell: (row) => <span className="font-semibold text-cs-dark">{row.load?.client_work_order || "-"}</span>,
        },
        {
            id: "description",
            name: "DESCRIPTION",
            selector: (row) => row.description || `Load #${row.load_id}`,
        },
        {
            id: "pickup",
            name: "PICK UP",
            selector: (row) => row.load?.pick_up_date || "-",
        },
        {
            id: "delivery",
            name: "DELIVERY",
            selector: (row) => row.load?.delivery_date || "-",
        },
        {
            id: "amount",
            name: "AMOUNT",
            cell: (row) => <span className="font-semibold text-cs-primary-dark">{currencyUSD(row.amount || 0)}</span>,
        },
    ];

    return (
        <>
            <div className="min-h-screen bg-cs-bg-alt py-8 px-4">
                <div className="max-w-auto mx-auto space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-cs-muted">Billing center</p>
                            <h1 className="text-2xl font-semibold text-cs-dark">Invoice detail</h1>
                            <p className="text-sm text-cs-muted mt-1">
                                Review the selected loads, totals and payment status for this customer invoice.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => navigate("/accounting")}
                                className="px-4 py-2 rounded-xl border border-cs-border text-sm text-cs-muted hover:bg-cs-bg"
                            >
                                Back
                            </button>
                            <CustomButtonOval onClick={getPdfDetailFile} type="button">
                                Generate PDF
                            </CustomButtonOval>
                        </div>
                    </div>

                    <div className="w-full bg-cs-bg rounded-2xl shadow-sm border border-cs-border overflow-hidden">
                        <div className="px-6 py-5 bg-cs-surface flex flex-col md:flex-row gap-4 md:gap-6 justify-between">
                            <div>
                                <h3 className="text-[10px] uppercase tracking-[0.14em] text-cs-muted mb-1">Invoice from</h3>
                                <p className="text-[11px] font-semibold text-cs-dark">CargoSmart / Your Company</p>
                            </div>
                            <div>
                                <h3 className="text-[10px] uppercase tracking-[0.14em] text-cs-muted mb-1">Invoice to</h3>
                                <p className="text-[11px] font-semibold text-cs-dark">{invoice.client_name || "-"}</p>
                            </div>
                            <div className="text-[9px] text-cs-muted space-y-1">
                                <p><span className="font-medium text-cs-dark">Invoice:</span> {invoice.invoice_number || "-"}</p>
                                <p><span className="font-medium text-cs-dark">Invoice date:</span> {invoice.invoice_date || "-"}</p>
                                <p><span className="font-medium text-cs-dark">Due date:</span> {invoice.due_date || "-"}</p>
                            </div>
                        </div>

                        <div className="px-4 py-3">
                            <DataTableElement
                                columns={summaryColumns}
                                items={invoice.id ? [invoice] : []}
                                tableLoad={tableLoad}
                            />
                        </div>

                        <div className="px-4 pb-4">
                            <div className="mt-3 mb-1 flex items-center justify-between">
                                <h3 className="text-[11px] font-semibold text-cs-dark">Selected loads</h3>
                                <span className="text-[8px] text-cs-muted">Loads included in this invoice</span>
                            </div>
                            <DataTableElement
                                columns={lineColumns}
                                items={invoice.loads || []}
                                tableLoad={tableLoad}
                                classHead="bg-cs-surface-alt"
                            />
                        </div>

                        <div className="px-4 md:px-6 py-4 border-t border-cs-border flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                            <div className="text-[10px] text-cs-muted">
                                <p>Subtotal: <span className="font-medium text-cs-dark">{currencyUSD(invoice.subtotal || 0)}</span></p>
                                <p>Tax: <span className="font-medium text-cs-dark">{currencyUSD(invoice.tax || 0)}</span></p>
                                <p className="text-[11px] font-semibold text-cs-dark mt-1">Total: {currencyUSD(invoice.total || 0)}</p>
                            </div>
                            {invoice.notes && (
                                <div className="text-[10px] text-cs-muted max-w-xl">
                                    <span className="font-medium text-cs-dark">Notes:</span> {invoice.notes}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {modalPdf.show && (
                <PreviewPDF modal={modalPdf} setModal={setModalPdf} />
            )}
        </>
    );
};

export default PaymentDetail;
